import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Stack, Typography, styled } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { CustomButton } from "@/components/custom/custom-button";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { DataTable } from "@/components/custom/data-table";
import { formatCurrency } from "@/utils/format-currency";
import { showToast } from "@/redux/actions/toast-actions";
import {
  useSmsCharges, useActiveSmsCharge, useToggleSmsCharge,
} from "@/hooks/use-notifications";
import { getChargeColumns } from "./_components/charge-columns";
import ChargeFormDialog from "./_components/charge-form-dialog";

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const SmsCharges = () => {
  const dispatch = useDispatch();
  const [createOpen, setCreateOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);

  const { data: charges, isLoading, isError } = useSmsCharges();
  const { data: activeCharge, isLoading: activeLoading } = useActiveSmsCharge();
  const toggleMutation = useToggleSmsCharge();

  const columns = useMemo(() => getChargeColumns({ onToggle: setToggleTarget }), []);

  const turningOff = toggleTarget?.active;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <CustomSimpleCard
          title="Active SMS charge"
          icon={<PaidOutlinedIcon fontSize="small" />}
          isLoading={activeLoading}
        >
          <Typography variant="h6">
            {activeCharge?.active && activeCharge?.fee != null
              ? formatCurrency(activeCharge.fee, "NGN")
              : "Not charging"}
          </Typography>
        </CustomSimpleCard>
      </Stack>

      <Card>
        <Stack direction="row" justifyContent="flex-end">
          <CustomButton
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setCreateOpen(true)}
            sx={{ height: 35 }}
          >
            Configure Charge
          </CustomButton>
        </Stack>

        <DataTable
          data={charges ?? []}
          columns={columns}
          isLoading={isLoading}
          isError={isError}
          noDataMessage="No SMS charges configured."
        />
      </Card>

      <ChargeFormDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <CustomConfirmDialog
        open={Boolean(toggleTarget)}
        onClose={() => setToggleTarget(null)}
        title={turningOff ? "Stop charging for SMS?" : "Apply this SMS charge?"}
        description={
          turningOff
            ? "Customers will no longer be billed for SMS alerts. Revenue from SMS charges stops until a charge is reactivated."
            : `Customers will be billed ${toggleTarget?.fee != null ? formatCurrency(toggleTarget.fee, "NGN") : "this fee"} per SMS alert. Any other active charge is replaced.`
        }
        confirmLabel={turningOff ? "Stop charging" : "Apply charge"}
        variant={turningOff ? "danger" : "warning"}
        mutation={toggleMutation}
        onConfirm={() =>
          toggleMutation.mutate(toggleTarget.id, {
            onSuccess: (res) =>
              dispatch(showToast(res?.message ?? "SMS charge updated", "success")),
            onError: (err) =>
              dispatch(showToast(err?.message ?? "Failed to update charge", "error")),
          })
        }
      />
    </Stack>
  );
};

export default SmsCharges;